import {formatBriefingRoleLabel} from "./briefing-audience-options";

export const BRIEF_READ_CTA = "I've read this briefing" as const;
/** @deprecated Gate uses Briefing #id title; kept for docs/native copy reference */
export const BRIEF_GATE_TITLE = "Action required before you continue" as const;
export const BRIEF_GATE_SUBTITLE =
  "You must read and confirm each briefing below to use the app." as const;

export function getBriefTitle(brief: string, createdByName?: string, createdAt?: string): string {
  const line = brief.trim().split(/\n/)[0]?.trim() ?? "";
  const preview = line.length > 72 ? `${line.slice(0, 72)}…` : line;
  if (preview) return preview;
  if (createdByName && createdAt) return `Brief from ${createdByName}`;
  return "Briefing";
}

export function getBriefSubtitle(id: number): string {
  return `Briefing #${id}`;
}

export function formatAudienceSummary(values: {
  target_all_properties: boolean;
  target_all_roles: boolean;
  property_ids: number[];
  role_keys: string[];
}): {headline: string; detail: string; isDefaultAudience: boolean} {
  const props = values.target_all_properties
    ? "All paid properties"
    : `${values.property_ids.length} propert${values.property_ids.length === 1 ? "y" : "ies"}`;
  const roles = values.target_all_roles
    ? "All eligible roles"
    : `${values.role_keys.length} role${values.role_keys.length === 1 ? "" : "s"}`;
  const isDefaultAudience = values.target_all_properties && values.target_all_roles;
  return {
    headline: `Send to ${props} · ${roles}`,
    detail: isDefaultAudience
      ? "Everyone with briefing access who matches the default audience will be notified."
      : "Only users matching these properties and roles will be notified. This cannot be changed after sending.",
    isDefaultAudience,
  };
}

export function formatAudienceSummaryFromBrief(briefingBoard: {
  target_all_properties: boolean;
  target_all_roles: boolean;
  target_property_ids: number[];
  target_role_keys: string[];
}): {headline: string; detail: string; isDefaultAudience: boolean} {
  return formatAudienceSummary({
    target_all_properties: briefingBoard.target_all_properties,
    target_all_roles: briefingBoard.target_all_roles,
    property_ids: briefingBoard.target_property_ids,
    role_keys: briefingBoard.target_role_keys,
  });
}

export type AudienceNameLookup = {
  propertyNamesById: ReadonlyMap<number, string>;
  roleNamesByKey: ReadonlyMap<string, string>;
};

export function createAudienceNameLookup(
  properties: {property_id: number; name: string}[] = [],
  roles: {key: string; name: string}[] = [],
): AudienceNameLookup {
  return {
    propertyNamesById: new Map(properties.map((p) => [p.property_id, p.name])),
    roleNamesByKey: new Map(roles.map((r) => [r.key, r.name])),
  };
}

export function formatTargetPropertiesLabel(
  row: {
    target_all_properties: boolean;
    target_property_ids: number[];
  },
  lookup: AudienceNameLookup,
): {display: string; tooltip: string} {
  if (row.target_all_properties) {
    return {display: "All", tooltip: "All paid properties"};
  }
  if (!row.target_property_ids?.length) {
    return {display: "—", tooltip: "—"};
  }
  const names = row.target_property_ids.map(
    (id) => lookup.propertyNamesById.get(id) ?? `Property #${id}`,
  );
  const joined = names.join(", ");
  return {display: joined, tooltip: joined};
}

export function formatTargetRolesLabel(
  row: {
    target_all_roles: boolean;
    target_role_keys: string[];
  },
  lookup: AudienceNameLookup,
): {display: string; tooltip: string} {
  if (row.target_all_roles) {
    return {display: "All", tooltip: "All eligible roles"};
  }
  if (!row.target_role_keys?.length) {
    return {display: "—", tooltip: "—"};
  }
  const names = row.target_role_keys.map(
    (key) => lookup.roleNamesByKey.get(key) ?? formatBriefingRoleLabel(key),
  );
  const joined = names.join(", ");
  return {display: joined, tooltip: joined};
}

export type AudienceTargetItem = {id: number; name: string};
export type AudienceRoleItem = {key: string; name: string};

export function getTargetPropertyItems(
  targetPropertyIds: number[],
  lookup: AudienceNameLookup,
): AudienceTargetItem[] {
  return (targetPropertyIds ?? []).map((id) => ({
    id,
    name: lookup.propertyNamesById.get(id) ?? `Property #${id}`,
  }));
}

export function getTargetRoleItems(
  targetRoleKeys: string[],
  lookup: AudienceNameLookup,
): AudienceRoleItem[] {
  return (targetRoleKeys ?? []).map((key) => ({
    key,
    name: lookup.roleNamesByKey.get(key) ?? formatBriefingRoleLabel(key),
  }));
}
